import { useEffect, useRef, useState } from 'react';
import { ArdoqId, APIWorkspaceAttributes } from '@ardoq/api-types';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { Tag, StatusType } from '@ardoq/status-ui';
import { Header2 } from '@ardoq/typography';
import { TruncatedTextPopover } from '@ardoq/popovers';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { IconButton } from '@ardoq/button';
import { colors } from '@ardoq/design-tokens';
import { Locale } from '@ardoq/locale';
import { MarkdownWithImagePreview } from '@ardoq/image-preview';
import { type I18nLocale, t } from '@ardoq/i18n';
import { WorkspacesDatasourceInformationFooter } from './WorkspacesDatasourceInformationFooter';
import type { GetWorkspaceManagerLinkProps } from './types';
import { getImagePreviewLabels } from './imagePreviewLabels';

export type SingleWorkspaceDatasourceInformationProps = {
  workspace: APIWorkspaceAttributes;
  canManageWorkspace: (workspaceId: ArdoqId) => boolean;
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps;
  locale: Locale;
  i18nLocale: I18nLocale;
};

export const SingleWorkspaceDatasourceInformation = ({
  workspace,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  locale,
  i18nLocale,
}: SingleWorkspaceDatasourceInformationProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () =>
      setShowFade(el.scrollHeight > el.clientHeight + el.scrollTop + 1);
    update();
    el.addEventListener('scroll', update);
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [isExpanded]);

  return (
    <Stack gap="small">
      <FlexBox gap="small" align="center" minWidth="0">
        <Icon
          iconName={IconName.WORKSPACE}
          iconSize={IconSize.MEDIUM}
          color={colors.icon}
        />
        <Box minWidth="0">
          <Header2>
            <TruncatedTextPopover>{workspace.name}</TruncatedTextPopover>
          </Header2>
        </Box>
        <Tag
          statusType={StatusType.DEFAULT}
          label={t(
            i18nLocale,
            'inventory:datasourceInformation.workspaceTag',
            'Workspace'
          )}
        />
        <IconButton
          label={
            isExpanded
              ? t(
                  i18nLocale,
                  'inventory:datasourceInformation.collapseWorkspaceInfo',
                  'Collapse workspace info'
                )
              : t(
                  i18nLocale,
                  'inventory:datasourceInformation.expandWorkspaceInfo',
                  'Expand workspace info'
                )
          }
          iconName={isExpanded ? IconName.CHEVRON_UP : IconName.CHEVRON_DOWN}
          onClick={() => setIsExpanded(prev => !prev)}
        />
      </FlexBox>

      {isExpanded && (
        <Stack gap="small">
          {workspace.description?.trim() && (
            <MarkdownWithImagePreview
              ref={scrollRef}
              content={workspace.description}
              sanitationLevel="permissive"
              style={{
                maxWidth: '65ch',
                maxHeight: '250px',
                overflowY: 'auto',
                ...(showFade && {
                  maskImage: 'linear-gradient(black 80%, transparent 100%)',
                  WebkitMaskImage:
                    'linear-gradient(black 80%, transparent 100%)',
                }),
              }}
              {...getImagePreviewLabels(i18nLocale)}
            />
          )}
          <WorkspacesDatasourceInformationFooter
            workspace={workspace}
            canManageWorkspace={canManageWorkspace}
            getWorkspaceManagerLinkProps={getWorkspaceManagerLinkProps}
            locale={locale}
            i18nLocale={i18nLocale}
          />
        </Stack>
      )}
    </Stack>
  );
};
